import { cleanLicence } from "@/lib/engagements/clubDetail";
import type { SeasonLicenceInput } from "./seasonLicence";

/** Les trois sous-onglets de Joueurs et Encadrement, dans l'ordre d'affichage.
 *  Même vocabulaire que `licence_state` en base : l'état est recalculé à chaque
 *  lecture, rien n'est stocké sur la personne. */
export type LicenceStateTab = "qualified" | "pending" | "departed";
export const LICENCE_STATE_TABS: readonly LicenceStateTab[] = ["qualified", "pending", "departed"];

/** `?etat=` → onglet. Toute valeur absente ou inconnue ouvre « Licenciés ». */
export function parseLicenceStateTab(value: string | null | undefined): LicenceStateTab {
  return value === "pending" || value === "departed" ? value : "qualified";
}

/** Les paramètres d'URL après un changement d'onglet. L'onglet par défaut
 *  n'écrit rien, pour que `/management/players` reste l'URL canonique ; les
 *  autres paramètres (`?player=`, `?staff=`, `?team=`) sont conservés. */
export function licenceStateSearchParams(current: URLSearchParams, next: LicenceStateTab): URLSearchParams {
  const params = new URLSearchParams(current);
  if (next === "qualified") params.delete("etat");
  else params.set("etat", next);
  return params;
}

export type LicenceCellView =
  | { kind: "current"; number: string | null }
  | { kind: "departed"; number: string | null; depositedByName: string | null; depositedByNameAr: string | null }
  | { kind: "not_relicensed"; lastNumber: string | null; lastSeason: string | null };

/** Le modèle de la cellule « Licence ».
 *
 *  - `current` : licence de la saison. Un `qualified` sans numéro reste
 *    `current` (tiret) : afficher « Aucune licence » dans l'onglet Licenciés
 *    contredirait l'onglet.
 *  - `departed` : licence de la saison déposée par un autre club, qu'on nomme.
 *  - `not_relicensed` : aucune licence de la saison. Le dernier numéro connu
 *    n'est qu'un rappel, jamais présenté comme acquis ; `prior` d'abord parce
 *    qu'il sait de quelle saison il vient, le scalaire sans saison ensuite. */
export function licenceCellView(subject: SeasonLicenceInput): LicenceCellView {
  const season = cleanLicence(subject.seasonLicenseNumber);
  if (subject.licenceState === "departed") {
    return {
      kind: "departed",
      number: season,
      depositedByName: subject.seasonLicenseTeamName ?? null,
      depositedByNameAr: subject.seasonLicenseTeamNameAr ?? null,
    };
  }
  if (season || subject.licenceState === "qualified") return { kind: "current", number: season };

  const prior = cleanLicence(subject.priorLicenseNumber);
  const scalar = cleanLicence(subject.licenseNumber);
  return {
    kind: "not_relicensed",
    lastNumber: prior ?? scalar,
    lastSeason: prior ? (subject.priorLicenseSeason ?? null) : null,
  };
}

export type LicenceStateCounts = Partial<Record<LicenceStateTab, number>>;

export interface ManagementTabCounts {
  players: number;
  staff: number;
  transfers: number;
  audit: number;
  playersByState: LicenceStateCounts;
  staffByState: LicenceStateCounts;
}

const num = (value: unknown): number | undefined => (typeof value === "number" && Number.isFinite(value) ? value : undefined);

function byState(raw: Record<string, unknown>, prefix: "players" | "staff"): LicenceStateCounts {
  const out: LicenceStateCounts = {};
  for (const tab of LICENCE_STATE_TABS) {
    const n = num(raw[`${prefix}_${tab}`]);
    if (n !== undefined) out[tab] = n;
  }
  return out;
}

/** La réponse de `get_management_tab_counts`, rendue sûre. Les badges de tête
 *  comptent les licenciés ; face à une RPC antérieure sans les six clés, ils
 *  retombent sur les anciens totaux et les sous-onglets restent indéfinis (« - »),
 *  jamais `0` ni `NaN`. */
export function normalizeManagementTabCounts(raw: unknown): ManagementTabCounts {
  const r = (typeof raw === "object" && raw !== null ? raw : {}) as Record<string, unknown>;
  return {
    players: num(r.players_qualified) ?? num(r.players) ?? 0,
    staff: num(r.staff_qualified) ?? num(r.staff) ?? 0,
    transfers: num(r.transfers) ?? 0,
    audit: num(r.audit) ?? 0,
    playersByState: byState(r, "players"),
    staffByState: byState(r, "staff"),
  };
}
